import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User, LogOut } from 'lucide-react';
import { debug } from '@/lib/utils';

type TradesPurpleLayoutProps = {
  children: ReactNode;
};

// Get a random but consistent image for the day
function getImageForToday() {
  const today = new Date();
  const dateKey = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  // Simple hash of the date to randomly but consistently select an image
  return dateKey % 2 === 0
    ? "/assets/tradespurple/tradespurple-illustration.png"
    : "/assets/tradespurple/TPman.png";
}

function TradesPurpleLayout({ children }: TradesPurpleLayoutProps) {
  const { isAuthenticated, user, signOut } = useAuth();
  const location = useLocation();

  // Check if user is on their account page (LoggedInLandingPage)
  const isAccountPage = isAuthenticated && location.pathname === '/';

  // Get image path based on today's date (will be stable for the whole day)
  const imageSrc = getImageForToday();
  const imageAlt = imageSrc.includes("TPman")
    ? "TradesPurple illustration showing a man using the service on his phone"
    : "TradesPurple illustration showing a woman using the service on her phone";

  const handleLogout = async () => {
    try {
      await signOut();
      // No need to navigate, AuthContext will handle that
    } catch (error) {
      debug.error('Logout failed:', error);
    }
  };

  return (
    <div className="max-w-md md:max-w-2xl mx-auto p-3 flex flex-col min-h-[100vh]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Top header with login/logout link */}
      <div className="absolute top-3 right-3 md:right-6">
        {isAuthenticated ? (
          isAccountPage ? (
            <button
              onClick={handleLogout}
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <LogOut className="h-5 w-5 mr-1" />
              <span className="text-sm">Log out</span>
            </button>
          ) : (
            <Link
              to="/"
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <User className="h-5 w-5 mr-1" />
              <span className="text-sm">My Page</span>
            </Link>
          )
        ) : (
          <Link
            to="/auth/login"
            className="flex items-center text-purple-800 hover:text-purple-900"
          >
            <User className="h-5 w-5 mr-1" />
            <span className="text-sm">Log in</span>
          </Link>
        )}
      </div>

      {/* TradesPurple hero section - clickable to tradespurple.com */}
      <a href="https://tradespurple.com" className="flex justify-center mb-1">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="h-64 max-w-full" /* Larger size as requested */
        />
      </a>

      {/* TradesPurple title */}
      <h1 className="text-3xl font-bold text-center mt-1 mb-1" style={{ color: "#5D4A9C" }}>
        TradesPurple
      </h1>
      {/* Only show subtitle on non-account pages */}
      {!isAccountPage && (
        <p className="text-center text-base md:text-lg text-purple-700 mt-1 mb-4 font-medium">
          Save, contact and share<br />
          the tradespeople you trust.
        </p>
      )}

      {/* Main Content - fills available space */}
      <div className="flex-1 flex flex-col">
        {children}
      </div>

      {/* Footer */}
      <footer className="mt-8 pt-3 border-t-2 border-purple-200 text-center text-gray-700 pb-safe">
        <div className="flex items-center justify-center">
          <div className="w-5 h-5 rounded-lg bg-purple-700 flex items-center justify-center mr-1">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium mr-1" style={{ color: "#5D4A9C" }}>Built by</p>
          <a
            href="/maxtb"
            style={{ color: "#5D4A9C" }}
            className="text-sm font-medium hover:underline"
          >
            Max
          </a>
        </div>
        <p className="mt-2 text-base text-purple-700 font-medium">
          Save, contact and share<br />
          the tradespeople you trust.
        </p>
      </footer>
    </div>
  );
}

// Single clear export
export default TradesPurpleLayout;
